import initialize, { interceptAxios, handleLogout, overwriteGlobalNamespace } from '@patsnap-utils/passport';
import { psAxios, setPostByFormPassport } from '@patsnap/common-ps-http';
import get from 'lodash/get';
import noop from 'lodash/noop';
import isRegExp from 'lodash/isRegExp';
import defer from 'promise-defer';
import { UserCenterService, SLD, Site } from '@patsnap-utils/biz';
import { PASSPORT_URI } from 'library-@/config/apiConfig';
import { currentClientUUID } from '@patsnap/uniform-setting';
import { applySignInterceptors } from './sign';
import { sleep } from '../startup/defer';
import qs from 'qs';

// 让头和身体保持同一个passport
if (!window.$passportGlobalNamespacePointer) {
    window.$passportGlobalNamespacePointer = {};
}
overwriteGlobalNamespace(window.$passportGlobalNamespacePointer);

const VUE_APP_PASSPORT_URI = localStorage.getItem('VUE_APP_PASSPORT_URI') || PASSPORT_URI;
const baseAuthServer = VUE_APP_PASSPORT_URI;
const clientId = process.env['VUE_APP_PASSPORT_CLIENT_ID'];
const clientName = process.env['VUE_APP_PASSPORT_CLIENT_NAME'];

let _header = {};

/**
 * 不需要鉴权的页面，
 * 但排除market这种同时要鉴权和不鉴权的页面，它们通过preRedirectLogin阻拦
 */
const IGNORE_PATHNAMES = [
    '/terms'
];

export const passportInterceptManager = {
    inject() {
        if (!psAxios.psInterceptors['passportRequestInterceptor']) {
            interceptAxios(psAxios);
        }
    },
    eject() {
        psAxios.interceptors.request.eject(psAxios.psInterceptors['passportRequestInterceptor']);
        psAxios.interceptors.response.eject(psAxios.psInterceptors['passportResponseInterceptor']);
        delete psAxios.psInterceptors['passportRequestInterceptor'];
        delete psAxios.psInterceptors['passportResponseInterceptor'];
    }
};

async function prepareTokenBase({ name, apiVersion, excludesUrlRegExps = [], callback = noop, preRedirectLogin = noop } = {}) {
    if (IGNORE_PATHNAMES.includes(window.location.pathname)) {
        return Promise.resolve();
    }
    const ignoreNodeInterceptor = excludesUrlRegExps === Infinity;
    const initializeConfig = {
        baseAuthServer,
        clientId,
        clientName,
        redirectToLogin(error) {
            preRedirectLogin(error);
            console.warn('Check Session Error, Will Redirect To Login Page!');
            stoper();
            const redirectUrl = get(error, 'data.redirect_url');
            if (redirectUrl) {
                window.location.href = redirectUrl + '&errorType=h1';
            } else if (error && error.errcode) {
                // 被踢出到登录页
                window.location.href = UserCenterService.createLogoutHref(true, {
                    errorCode: error.errcode,
                    errorType: 'h2'
                });
            } else {
                // 其他需要登陆的操作
                window.location.href = UserCenterService.createLogoutHref(true, {
                    errorCode: 39999,
                    errorType: 'h3'
                });
            }
            return sleep(6000); // 正在跳转（大概6秒够了），后面的别执行了
        },
        syncTokenParamsModifier(params) { // 可选，允许修改checksession的参数
            return { ...params, flag: 'analytics' };
        },
        detectResponseAsUnauthorized(error) {
            let redirectUrl = get(error, 'response.data.redirectUrl');
            if (redirectUrl) {
                stoper();
                window.location.href = redirectUrl;
                throw error;
            }
            return get(error, 'response.status') === 401;
        },
        onPaymentRequired(error) {
            // 自己跳转到付费页面
            const errcode = get(error, 'errcode');
            const urlParams = {
                from: clientName,
                client_id: clientId,
                redirect_uri: encodeURIComponent(window.location.href),
                errorCode: errcode
            };
            const url = new Site({ sld: SLD.ACCOUNT, pathname: `user-settings/#/upper-limit${qs.stringify(urlParams, { addQueryPrefix: true })}` }).href
            stoper();
            window.location.replace(url);
        },
        async onMfaRequired(err) {
            const urlParams = {
                from: clientName,
                client_id: clientId,
                redirect_uri: encodeURIComponent(window.location.href),
                errorCode: get(err, 'errcode')
            }
            const url = new Site({ sld: SLD.ACCOUNT, pathname: `user-settings/#/mfa-required${qs.stringify(urlParams, { addQueryPrefix: true })}` }).href
            stoper();
            window.location.replace(url);
          },
    };

    /* 清除 csrfTokenInterceptor */
    psAxios.interceptors.request.eject(psAxios.psInterceptors.csrfTokenInterceptor);

    if (!name) {
        throw new Error('options.name is required');
    }
    // 拦截node api
    const interceptorRequestSuccess = config => {

        config.headers['X-PatSnap-From'] = `w-analytics-${name}`;
        config.headers['X-Device-ID'] = currentClientUUID();

        config.headers = {
            ...config.headers,
            ..._header,
        };

        if (ignoreNodeInterceptor) return config;
        if (/\/node-internal\/.+/.test(config.url)) return config;

        const excludesUrlConfig = handleExcludesUrlRegExps({
            config,
            excludesUrlRegExps: excludesUrlRegExps.concat(/http(s)?:\/\//),
            apiVersion,
        });

        if (excludesUrlConfig) return excludesUrlConfig;

        if (!(config.url.startsWith('//') || config.url.startsWith('http'))) {
            config.url = `/node-internal${config.url}`;
        }

        return config;
    };

    psAxios.psInterceptors.nodeApiInterceptor = psAxios.interceptors.request.use(interceptorRequestSuccess, null);

    passportInterceptManager.inject();

    window.customBeforeLogout = function customBeforeLogout() {
        // 此方法默认会被 Header V4点"登出"时调用， 用其他头时，登出时自己调用 handleLogout()
        return handleLogout();
    };

    callback(psAxios);

    const passport = await initialize(initializeConfig);

    window[revertString('6csg9md32h')] = function () {
        return passport.getToken('token') || '';
    };

    applySignInterceptors(psAxios, passport);

    setPostByFormPassport(passport);
    return passport;
}

export function stoper() {
    if (window.location.search.indexOf('noraven') > -1) {
        throw new Error('Local stop redirect');
    }
}

/**
 * 更新 tokenbase 请求的 header， 如 X-Site-Lang
 */
function updateTokenBaseRequestHeaders(headers) {
    _header = headers;
}

const headerDefer = () => {
    if (!window.deferredApiComponentsHeader) {
        window.deferredApiComponentsHeader = defer();
    }
    setTimeout(function () {
        window.deferredApiComponentsHeader.resolve();
    }, 10 * 1000);
    return window.deferredApiComponentsHeader.promise;
};

const resolveHeader = (data) => {
    headerDefer();
    window.deferredApiComponentsHeader.resolve();
    return data;
};

const handleExcludesUrlRegExps = ({ config, excludesUrlRegExps, apiVersion }) => {
    if (excludesUrlRegExps === Infinity) {
        return;
    }
    for (let rule of excludesUrlRegExps) {
        const pattern = get(rule, 'pattern') || rule;
        const version = get(rule, 'version') || apiVersion;
        if (!isRegExp(pattern)) {
            console.warn('Error excludes url RegExp: ', rule, config, excludesUrlRegExps, apiVersion);
            continue;
        }
        if (pattern.test(config.url)) {
            /**
             * 如果 API （封装）未指定 header: X-API-Version，才使用 preset 中配置的 version
             */
            if (!('X-API-Version' in config.headers) && version) {
                config.headers['X-API-Version'] = version;
            }
            return config;
        }
    }
};

export {
    baseAuthServer,
    headerDefer,
    resolveHeader,
    prepareTokenBase,
    updateTokenBaseRequestHeaders,
    handleExcludesUrlRegExps,
};

function revertString(str) {
    return str.split('').reverse().join('');
}
